<template>
  <div class="w-full relative">
    <!-- profile actions slot -->
    <div class="absolute right-4 top-2 sm:right-6 sm:top-6">
      <slot />
    </div>
    <!-- profile information -->
    <div class="sm:mx-36 pt-14 sm:pt-6">
      <div v-if="state.user" id="user-info" class="flex flex-col mx-6 mb-6 space-y-2">
        <!-- user information -->
        <div class="flex items-center flex-wrap">
          <h1 class="font-bold truncate mr-2" :class="[textStyle, state.user.name ? 'text-20' : 'text-sm']">
            {{ state.user.name ? state.user.name : '@' + state.user.username }}
          </h1>
          <div v-if="state.user.name" class="font-medium text-sm text-primary-200 mr-2">@{{ state.user.username }}</div>
          <div
            v-if="state.user.title"
            class="flex flex-col items-start rounded-md sm:rounded-full text-md text-primary-200 font-bold w-full sm:w-auto mt-2 sm:mt-0"
          >
            <div class="h-8 px-3 inline-flex items-center rounded-full border border-divider-100" :class="textStyle">
              <div class="mr-2">{{ state.user.title }}</div>
              <OIcon :icon="textStyle === 'text-primary-200' ? 'check-circle' : 'check-circle-colored'" />
            </div>
          </div>
        </div>

        <!-- following-follower information -->
        <div v-if="!blocked && !blockedMe" class="space-y-2">
          <div v-if="!isFollowPage && isMobile" class="flex items-center space-x-4 text-13 mt-3 mb-4">
            <nuxt-link :to="followerCount > 0 ? `/${PROFILE_PATH}/${username}/${FOLLOWERS_PATH}` : ''">
              <span class="text-black font-bold">{{ followerCount }}</span>
              {{ $t('profile.followers') }}
            </nuxt-link>
            <nuxt-link :to="followingCount > 0 ? `/${PROFILE_PATH}/${username}/${FOLLOWINGS_PATH}` : ''">
              <span class="text-black font-bold">{{ followingCount }}</span>
              {{ $t('profile.follows') }}
            </nuxt-link>
          </div>
          <div v-else class="flex items-center space-x-6 text-md">
            <OButton pure class="hover:text-link-primary" @click="openFollowModal('following')">
              {{ $t('profile.follows') }}
              <span class="font-bold">{{ followingCount }}</span>
            </OButton>
            <OButton pure class="hover:text-link-primary" @click="openFollowModal('followers')">
              {{ $t('profile.followers') }}
              <span class="font-bold">{{ followerCount }}</span>
            </OButton>
          </div>

          <!-- user location, website, instagram information -->
          <div class="flex flex-wrap items-center flex-wrapx gap-4 -m-0.5 break-word">
            <div v-if="state.user.country && state.user.city" class="text-md font-normal text-primary-200 flex items-center">
              <OIcon icon="location" class="mr-0.5" />
              {{ state.user.country + ', ' + state.user.city }}
            </div>
            <div v-if="hasWebsite" class="flex items-center text-link-primary text-sm">
              <OIcon icon="link" class="mr-0.5" />
              {{ state.user.website.replace('https://', '') }}
            </div>
            <div v-if="state.user.instagramUsername" class="flex items-center text-link-primary text-sm">
              <OIcon icon="article/instagram" class="mr-0.5 text-primary-200" />
              {{ state.user.instagramUsername }}
            </div>
          </div>
        </div>
      </div>

      <!-- TODO:// premium social widgets -->
      <div v-if="false && isPremiumUser">
        <TwitterTimelineWidget class="mt-8" :user="state.user" />
        <FacebookTimelineWidget class="mt-8" :user="state.user" show-timeline />
        <InstagramWidget class="mt-8" :user="state.user" />
      </div>
    </div>

    <!-- profile description -->
    <div v-if="state.user.sideText && !blocked && !blockedMe" class="m-6 text-lg font-normal">
      <div class="text-gray-700 whitespace-pre-wrap break-words">{{ state.user.sideText }}</div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, useRoute } from '@nuxtjs/composition-api';
import { useDevice } from '@/composables/useDevice';
import { useProfile } from '@/modules/profile/state/useProfile';
import { FOLLOWERS_PATH, FOLLOWINGS_PATH, PROFILE_PATH } from '~/constants/config';
import { isLink } from '~/modules/common/utils/stringUtils';
import { OButton, OIcon } from '~/modules/common/components/ui';
import TwitterTimelineWidget from '~/modules/profile/components/TwitterTimelineWidget';
import FacebookTimelineWidget from '~/modules/profile/components/FacebookTimelineWidget';
import InstagramWidget from '~/modules/profile/components/InstagramWidget';
import { useModal } from '~/composables/useModal';

export default defineComponent({
  components: {
    InstagramWidget,
    FacebookTimelineWidget,
    TwitterTimelineWidget,
    OButton,
    OIcon,
  },
  setup() {
    const { isMobile } = useDevice();
    const { openModal } = useModal();
    const route = useRoute();
    const { state } = useProfile();
    const username = route.value.params.username;
    const isPremiumUser = computed(() => state.value.user?.isPremium);

    const isFollowPage = computed(() => route.value.path.includes(FOLLOWERS_PATH) || route.value.path.includes(FOLLOWINGS_PATH));
    const followerCount = computed(() => state.value.user?.stats.followers);
    const followingCount = computed(() => state.value.user?.stats.following);

    const hasWebsite = isLink(state.value.user?.website);

    // color schema
    const textMap = {
      user: 'text-primary-200',
      writer: 'text-[#3EA7F5]',
    };
    const role = ['Onedio Üyesi', 'Onedio Member', 'Пользователь Onedio'].includes(state.value.user?.title) ? 'user' : 'writer';
    const textStyle = textMap[role];

    const blocked = computed(() => state.value.user?.blockStatus);
    const blockedMe = computed(() => state.value.user?.blocked);

    const openFollowModal = (type) => {
      if ((type === 'following' && followingCount.value === 0) || (type === 'followers' && followerCount.value === 0)) return;
      openModal(import('~/modules/profile/components/ProfileView/ProfileFollowModal' /* webpackChunkName: "ProfileFollowModal" */), {
        type,
      });
    };

    return {
      isMobile,
      state,
      isFollowPage,
      username,
      followerCount,
      followingCount,
      FOLLOWINGS_PATH,
      FOLLOWERS_PATH,
      PROFILE_PATH,
      isPremiumUser,
      hasWebsite,
      textStyle,
      blocked,
      blockedMe,
      openFollowModal,
    };
  },
});
</script>
